import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useWhatsAppComposer } from '../whatsAppMessage/useWhatsAppComposer';
import { WhatsAppFileChip } from '../whatsAppMessage/WhatsAppFileChip';
import { WhatsAppTemplatePicker } from '../whatsAppMessage/WhatsAppTemplatePicker';

/**
 * WHATSAPP INLINE COMPOSER — la riga di scrittura sotto la chat: testo, allegato,
 * Invio. Se la finestra di Meta è chiusa il backend risponde 409 e compare il
 * popup del modello approvato. Lo usano la sessione nella scheda del contatto e
 * la pagina WhatsApp (FILO-37).
 */
interface WhatsAppInlineComposerProps {
  personId: number;
  /** Numero del contatto a cui si scrive. */
  phoneNumber: string;
  /** Numero business mittente; vuoto = il predefinito del backend. */
  phoneNumberId?: string;
  /** Dopo un invio riuscito, testo o modello: di solito ricarica le sessioni. */
  onSent: () => Promise<unknown> | void;
  /** In testa alla riga, prima del testo: la pagina WhatsApp ci mette il mittente. */
  leading?: ReactNode;
}

export function WhatsAppInlineComposer({
  personId,
  phoneNumber,
  phoneNumberId,
  onSent,
  leading,
}: WhatsAppInlineComposerProps) {
  const { t } = useTranslation();
  const {
    file,
    inputProps,
    openFilePicker,
    clearFile,
    submitting,
    send,
    windowConflict,
    dismissWindowConflict,
    pendingBody,
    sendTemplate,
  } = useWhatsAppComposer();

  const [body, setBody] = useState('');

  const canSubmit = (body.trim().length > 0 || file !== null) && personId > 0;

  async function sent() {
    setBody('');
    clearFile();
    await onSent();
  }

  async function submit() {
    if (!canSubmit || submitting) return;

    const ok = await send({
      personId,
      phoneNumber,
      body,
      phoneNumberId: phoneNumberId || undefined,
    });
    if (ok) await sent();
  }

  async function submitTemplate(
    template: Parameters<typeof sendTemplate>[0],
    parameters: Parameters<typeof sendTemplate>[1],
  ) {
    if (await sendTemplate(template, parameters)) await sent();
  }

  return (
    <>
      <form
        className="whatsapp-composer"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void submit();
        }}
      >
        {file && <WhatsAppFileChip fileName={file.name} onRemove={clearFile} />}
        <div className="whatsapp-composer__row">
          <input {...inputProps} />
          {leading}
          <TextField
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              file ? t('whatsapp.captionPlaceholder') : t('whatsapp.replyPlaceholder')
            }
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            size="small"
            className="whatsapp-composer__input"
            onKeyDown={(e) => {
              // Invio = invia; Shift+Invio = a capo.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
          />
          <IconButton
            type="button"
            size="small"
            aria-label={t('whatsapp.attach')}
            className="whatsapp-composer__attach"
            onClick={openFilePicker}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !canSubmit}
            className="whatsapp-composer__send"
          >
            {t('whatsapp.send')}
          </Button>
        </div>
      </form>
      {windowConflict && (
        <WhatsAppTemplatePicker
          conflict={windowConflict}
          pendingBody={pendingBody()}
          submitting={submitting}
          onClose={dismissWindowConflict}
          onSend={(template, parameters) => void submitTemplate(template, parameters)}
        />
      )}
    </>
  );
}

export default WhatsAppInlineComposer;
